import { MenuItem } from "@mui/material";
import * as React from "react";

import { ContextMenu, MenuOptions } from "@/shared/components/ContextMenu";
import { Menu } from "@/shared/components/Menu";
import {
  useUpdateUserConfig,
  useUserConfig,
} from "@/shared/domain/config/hooks";
import {
  createEmptyLayout,
  isTemporaryLayout,
  LayoutRecord,
} from "@/shared/domain/layouts";
import { useLayouts } from "@/shared/domain/layouts/hooks";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";
import { usePopupState } from "@/shared/hooks";
import { EventName } from "@/shared/services/analytics";
import { useVizState } from "@/shared/state/visualization";

import {
  useActiveLayout,
  useWorkspaceSnapshot,
} from "../../WorkspaceCtx/hooks";

import { CreateFolderModal } from "./CreateFolderModal";
import { CreateLayoutFormModal } from "./CreateLayoutFormModal";
import { DeleteFolderConfirmation } from "./DeleteFolderConfirmation";
import { DeleteLayoutConfirmation } from "./DeleteLayoutConfirmation";
import { collectFolderNames, LayoutFolderGroup } from "./layoutFolders";
import { MoveLayoutModal } from "./MoveLayoutModal";
import { RenameFolderModal } from "./RenameFolderModal";
import { RenameLayoutModal } from "./RenameLayoutModal";
import { SaveLayoutConfirmation } from "./SaveLayoutConfirmation";
import { SelectLayoutMenu } from "./SelectLayoutMenu";
import { ShareLayoutConfirmation } from "./ShareLayoutConfirmation";

interface SelectLayoutProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
}

export function SelectLayout(props: SelectLayoutProps) {
  const layoutMenu = {
    anchor: props.anchorEl,
    close: props.onClose,
    isOpen: props.isOpen,
  };

  const {
    contextMenu,
    folderMenu,
    existingFolders,
    modalState,
    moveState,
    deleteState,
    createFolderState,
    folderModal,
    handlers,
    toContextOptions,
  } = useLayoutMenuController(layoutMenu);

  return (
    <>
      <SelectLayoutMenu
        anchorEl={layoutMenu.anchor}
        isOpen={layoutMenu.isOpen}
        onClose={layoutMenu.close}
        onOpenContextMenu={handlers.onOpenContextMenu}
        onOpenFolderMenu={handlers.onOpenFolderMenu}
        onSaveLayout={handlers.onSaveLayout}
        onSelectLayout={handlers.onSelectLayout}
        onMoveSelected={handlers.onMoveSelected}
        onDeleteSelected={handlers.onDeleteSelected}
        onRenameLayout={handlers.onRenameLayout}
        onShareLayout={handlers.onShareLayout}
        onNewFolder={handlers.onNewFolder}
      />

      <ContextMenu
        anchorEl={contextMenu.anchor}
        isOpen={contextMenu.isOpen}
        onClose={contextMenu.close}
        options={
          contextMenu.metadata ? toContextOptions(contextMenu.metadata) : []
        }
      />

      <Menu
        anchorEl={folderMenu.anchor}
        open={folderMenu.isOpen}
        onClose={folderMenu.close}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          disableRipple
          sx={{ typography: "body2" }}
          onClick={() =>
            folderMenu.metadata && handlers.onRenameFolder(folderMenu.metadata)
          }
        >
          Rename folder
        </MenuItem>
        <MenuItem
          disableRipple
          sx={{ typography: "body2", color: "error.main" }}
          onClick={() =>
            folderMenu.metadata && handlers.onDeleteFolder(folderMenu.metadata)
          }
        >
          Delete folder
        </MenuItem>
      </Menu>

      <CreateLayoutFormModal
        isOpen={modalState.type === "create"}
        existingFolders={existingFolders}
        onClose={handlers.onCloseModal}
        onSuccess={handlers.onCreateLayoutSuccess}
      />

      <DeleteLayoutConfirmation
        layouts={deleteState.layouts}
        isOpen={deleteState.isOpen}
        onClose={handlers.onCloseDelete}
        onExited={handlers.onDeleteExited}
        onSuccess={handlers.onDeleteSuccess}
      />

      <CreateFolderModal
        layouts={createFolderState.layouts}
        existingFolders={existingFolders}
        isOpen={createFolderState.isOpen}
        onClose={handlers.onCloseCreateFolder}
        onSuccess={handlers.onCreateFolderSuccess}
      />

      <RenameLayoutModal
        layout={modalState.layout}
        isOpen={modalState.type === "rename"}
        onClose={handlers.onCloseModal}
        onExited={handlers.onModalExited}
        onSuccess={handlers.onRenameLayoutSuccess}
      />

      <MoveLayoutModal
        layouts={moveState.layouts}
        existingFolders={existingFolders}
        isOpen={moveState.isOpen}
        onClose={handlers.onCloseMove}
        onExited={handlers.onMoveExited}
        onSuccess={handlers.onMoveSuccess}
      />

      <RenameFolderModal
        group={folderModal.group}
        existingFolders={existingFolders}
        isOpen={folderModal.type === "rename"}
        onClose={handlers.onCloseFolderModal}
        onExited={handlers.onFolderModalExited}
        onSuccess={handlers.onFolderModalSuccess}
      />

      <DeleteFolderConfirmation
        group={folderModal.group}
        isOpen={folderModal.type === "delete"}
        onClose={handlers.onCloseFolderModal}
        onExited={handlers.onFolderModalExited}
        onSuccess={handlers.onFolderModalSuccess}
      />

      <SaveLayoutConfirmation
        isOpen={modalState.type === "save"}
        layout={modalState.layout}
        onClose={handlers.onCloseModal}
        onExited={handlers.onModalExited}
        onAnswered={handlers.onAnsweredSaveLayout}
      />

      <ShareLayoutConfirmation
        layout={modalState.layout}
        isOpen={modalState.type === "share"}
        onClose={handlers.onCloseModal}
        onExited={handlers.onModalExited}
      />
    </>
  );
}

type ModalState = {
  type: "create" | "save" | "share" | "rename" | null;
  layout: LayoutRecord | null;
};

type MoveState = {
  isOpen: boolean;
  layouts: LayoutRecord[];
};

type DeleteState = {
  isOpen: boolean;
  layouts: LayoutRecord[];
};

type FolderModalState = {
  type: "rename" | "delete" | null;
  group: LayoutFolderGroup | null;
};

function useLayoutMenuController(layoutMenu: { close: () => void }) {
  const contextMenu = usePopupState<LayoutRecord>();
  const folderMenu = usePopupState<LayoutFolderGroup>();
  const [modalState, setModalState] = React.useState<ModalState>({
    type: null,
    layout: null,
  });
  const [moveState, setMoveState] = React.useState<MoveState>({
    isOpen: false,
    layouts: [],
  });
  const [deleteState, setDeleteState] = React.useState<DeleteState>({
    isOpen: false,
    layouts: [],
  });
  const [createFolderOpen, setCreateFolderOpen] = React.useState(false);
  const [folderModal, setFolderModal] = React.useState<FolderModalState>({
    type: null,
    group: null,
  });
  const [activeLayout, setActiveLayout] = useActiveLayout();
  const vizState = useVizState();
  const [, setWorkspaceSnapshot] = useWorkspaceSnapshot();

  const defaultLayoutQuery = useUserConfig("user_default_layout_id");

  const setDefaultLayoutMutation = useUpdateUserConfig();

  const layoutsQuery = useLayouts();
  const existingFolders = collectFolderNames(layoutsQuery.data ?? []);

  // Refresh the active layout reference so metadata changes (name, folder) show. skipReload:
  // these fields don't affect panel contents, so don't re-hydrate the workspace.
  const syncActiveLayout = (updated: LayoutRecord[]) => {
    const match = updated.find(
      (layout) => layout.layout_id === activeLayout?.layout_id,
    );
    if (match) {
      setActiveLayout(match, { skipReload: true });
    }
  };

  const handleOpenContextMenu = (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => {
    // Stop the row click from also loading the layout.
    event.stopPropagation();

    contextMenu.open({
      anchorEl: event.currentTarget.parentElement,
      metadata: layout,
    });
  };

  const handleOpenFolderMenu = (
    event: React.MouseEvent<HTMLElement>,
    group: LayoutFolderGroup,
  ) => {
    event.stopPropagation();
    folderMenu.open({ anchorEl: event.currentTarget, metadata: group });
  };

  const handleCreateLayout = () => {
    setModalState({ type: "create", layout: null });
    layoutMenu.close();
  };

  const handleSaveLayout = () => {
    if (!activeLayout || isTemporaryLayout(activeLayout)) {
      handleCreateLayout();
    } else {
      setModalState({ type: "save", layout: activeLayout });
      layoutMenu.close();
    }
  };

  const handleSelectLayout = (layout: LayoutRecord) => {
    setActiveLayout(layout);
    layoutMenu.close();
  };

  const handleShareLayout = (layout: LayoutRecord) => {
    setModalState({ type: "share", layout });
    contextMenu.close();
  };

  const handleRenameLayout = (layout: LayoutRecord) => {
    setModalState({ type: "rename", layout });
    contextMenu.close();
  };

  const openMove = (layouts: LayoutRecord[]) => {
    setMoveState({ isOpen: true, layouts });
  };

  const handleMoveLayout = (layout: LayoutRecord) => {
    openMove([layout]);
    contextMenu.close();
  };

  const handleMoveSelected = (layouts: LayoutRecord[]) => {
    openMove(layouts);
    layoutMenu.close();
  };

  const handleCloseMove = () => {
    setMoveState((state) => ({ ...state, isOpen: false }));
  };

  const handleMoveExited = () => {
    setMoveState({ isOpen: false, layouts: [] });
  };

  const handleMoveSuccess = (updated: LayoutRecord[]) => {
    syncActiveLayout(updated);
    handleCloseMove();
  };

  const handleRenameFolder = (group: LayoutFolderGroup) => {
    setFolderModal({ type: "rename", group });
    folderMenu.close();
  };

  const handleDeleteFolder = (group: LayoutFolderGroup) => {
    setFolderModal({ type: "delete", group });
    folderMenu.close();
  };

  const handleCloseFolderModal = () => {
    setFolderModal((state) => ({ ...state, type: null }));
  };

  const handleFolderModalExited = () => {
    setFolderModal((state) => ({ ...state, group: null }));
  };

  const handleFolderModalSuccess = (updated: LayoutRecord[]) => {
    syncActiveLayout(updated);
    handleCloseFolderModal();
  };

  const openDelete = (layouts: LayoutRecord[]) => {
    setDeleteState({ isOpen: true, layouts });
  };

  const handleDeleteLayout = (layout: LayoutRecord) => {
    openDelete([layout]);
    contextMenu.close();
  };

  const handleDeleteSelected = (layouts: LayoutRecord[]) => {
    openDelete(layouts);
    layoutMenu.close();
  };

  const handleCloseDelete = () => {
    setDeleteState((state) => ({ ...state, isOpen: false }));
  };

  const handleDeleteExited = () => {
    setDeleteState({ isOpen: false, layouts: [] });
  };

  const handleDeleteSuccess = (deleted: LayoutRecord[]) => {
    // If the active layout was among those deleted, clear it. skipReload keeps the current
    // workspace on screen instead of re-hydrating from the empty layout.
    if (
      deleted.some((layout) => layout.layout_id === activeLayout?.layout_id)
    ) {
      setActiveLayout(createEmptyLayout(), { skipReload: true });
    }
    handleCloseDelete();
  };

  const handleNewFolder = () => {
    // Leave the layout menu open behind the dialog (like the three-dot "Move to folder" flow),
    // so dismissing the dialog drops the user back into the menu.
    setCreateFolderOpen(true);
  };

  const handleCloseCreateFolder = () => {
    setCreateFolderOpen(false);
  };

  const handleCreateFolderSuccess = (updated: LayoutRecord[]) => {
    syncActiveLayout(updated);
    handleCloseCreateFolder();
  };

  const handleCloseModal = () => {
    setModalState((state) => ({ ...state, type: null }));
  };

  const handleModalExited = () => {
    setModalState((state) => ({ ...state, layout: null }));
  };

  const handleCreateLayoutSuccess = (newLayout: LayoutRecord) => {
    setActiveLayout(newLayout, { skipReload: true });
    setWorkspaceSnapshot({
      panels: vizState.panels,
      layout: vizState.layout,
    });
    handleCloseModal();
  };

  const handleOnAnsweredSaveLayout = (
    answer: { shouldOverwrite: boolean },
    newLayout?: LayoutRecord,
  ) => {
    if (answer.shouldOverwrite && newLayout) {
      handleCloseModal();
      setActiveLayout(newLayout, { skipReload: true });
      setWorkspaceSnapshot({
        panels: vizState.panels,
        layout: vizState.layout,
      });
    } else {
      handleCreateLayout();
    }
  };

  const handleRenameLayoutSuccess = (updatedLayout: LayoutRecord) => {
    syncActiveLayout([updatedLayout]);
    handleCloseModal();
  };

  const handleSetAsDefault = (layout: LayoutRecord) => {
    setDefaultLayoutMutation.mutate({
      key: "user_default_layout_id",
      value: layout.layout_id,
    });
    contextMenu.close();
  };

  const handleRemoveAsDefault = () => {
    setDefaultLayoutMutation.mutate({
      key: "user_default_layout_id",
      value: "",
    });
    contextMenu.close();
  };

  const toContextOptions = (layout: LayoutRecord): MenuOptions[] => {
    const option = (
      eventName: EventName,
      label: string,
      onClick: () => void,
      style?: Record<string, unknown>,
    ): MenuOptions => ({
      eventName,
      eventProperties: { layoutId: layout.layout_id, layoutName: layout.name },
      label,
      onClick,
      style: { typography: "body2", ...style },
    });

    const isCurrentDefault = defaultLayoutQuery.data === layout.layout_id;
    const defaultOption = isCurrentDefault
      ? option(
          "RemoveAsDefaultLayoutOptionClicked",
          "Remove as default",
          handleRemoveAsDefault,
        )
      : option("SetAsDefaultLayoutOptionClicked", "Set as default", () =>
          handleSetAsDefault(layout),
        );

    const options = [defaultOption];

    if (layout.accessibility === LayoutAccessibility.User) {
      options.push(
        option("ShareLayoutOptionClicked", "Share with team", () =>
          handleShareLayout(layout),
        ),
      );
    }

    options.push(
      option("RenameLayoutOptionClicked", "Rename", () =>
        handleRenameLayout(layout),
      ),
      option("MoveLayoutOptionClicked", "Move to folder", () =>
        handleMoveLayout(layout),
      ),
      option(
        "DeleteLayoutOptionClicked",
        "Delete",
        () => handleDeleteLayout(layout),
        {
          color: "error.main",
        },
      ),
    );

    return options;
  };

  return {
    contextMenu,
    folderMenu,
    existingFolders,
    modalState,
    moveState,
    deleteState,
    createFolderState: {
      isOpen: createFolderOpen,
      layouts: layoutsQuery.data ?? [],
    },
    folderModal,
    handlers: {
      onCloseModal: handleCloseModal,
      onCreateLayoutSuccess: handleCreateLayoutSuccess,
      onModalExited: handleModalExited,
      onCloseMove: handleCloseMove,
      onMoveExited: handleMoveExited,
      onMoveSuccess: handleMoveSuccess,
      onMoveSelected: handleMoveSelected,
      onCloseDelete: handleCloseDelete,
      onDeleteExited: handleDeleteExited,
      onDeleteSuccess: handleDeleteSuccess,
      onDeleteSelected: handleDeleteSelected,
      onNewFolder: handleNewFolder,
      onCloseCreateFolder: handleCloseCreateFolder,
      onCreateFolderSuccess: handleCreateFolderSuccess,
      onRenameLayout: handleRenameLayout,
      onShareLayout: handleShareLayout,
      onOpenContextMenu: handleOpenContextMenu,
      onOpenFolderMenu: handleOpenFolderMenu,
      onRenameFolder: handleRenameFolder,
      onDeleteFolder: handleDeleteFolder,
      onCloseFolderModal: handleCloseFolderModal,
      onFolderModalExited: handleFolderModalExited,
      onFolderModalSuccess: handleFolderModalSuccess,
      onRenameLayoutSuccess: handleRenameLayoutSuccess,
      onSaveLayout: handleSaveLayout,
      onAnsweredSaveLayout: handleOnAnsweredSaveLayout,
      onSelectLayout: handleSelectLayout,
    },
    toContextOptions,
  };
}
